import type { Feature, Plan } from '../api/types';

/**
 * Speiling av funksjonsmatrisen i API-et.
 *
 * The server is the authority — this copy only decides what the interface shows
 * (locks, badges, upgrade prompts) so the UI does not have to wait for a 402.
 */

export const PLAN_RANK: Record<Plan, number> = { free: 0, standard: 1, plus: 2 };

/**
 * Hvor lenge Plus er gratis for en ny konto, regnet fra registreringen.
 *
 * Speilet fra `PLUS_PROVE_DAGER` i API-et, som er det som faktisk gir
 * perioden. `tests/proveperiode.test.ts` holder de to like: en prisside som
 * lover fem uker når kontoen får fire, er markedsføring med feil tilbud.
 */
export const PLUS_PROVE_DAGER = 28;

/**
 * «4 uker». Lengden på gratisperioden slik den skrives til kunden.
 *
 * Hardt mellomrom mellom tallet og ordet: i en smal overskrift ble «4» stående
 * alene på slutten av linja og «uker» på neste.
 */
export function provelengde(dager: number = PLUS_PROVE_DAGER): string {
  if (dager % 7 !== 0) return `${dager}\u00a0dager`;
  const uker = dager / 7;
  return `${uker}\u00a0${uker === 1 ? 'uke' : 'uker'}`;
}

/**
 * Pakken de har tilgang til, med gratisperioden regnet inn.
 *
 * Speiling av `gjeldendePlan` i API-et. Grensesnittet får vanligvis svaret
 * ferdig fra `/auth/me`; dette trengs der det bare kjenner det som er betalt
 * for – rett etter et kjøp – og må vite hva det betyr i dag. Har de kjøpt
 * Standard midt i perioden, skal ikke bordplasseringen låses i menyen før
 * perioden faktisk er over.
 */
export function gjeldendePlan(
  betalt: Plan,
  plusProveTil: string | null | undefined,
  naa: Date = new Date(),
): Plan {
  if (plusProveTil && new Date(plusProveTil) > naa && PLAN_RANK.plus > PLAN_RANK[betalt]) {
    return 'plus';
  }
  return betalt;
}

export const FEATURE_MIN_PLAN: Record<Feature, Plan> = {
  guests: 'free',
  rsvp: 'free',
  budget: 'free',
  tasks: 'free',
  seating: 'plus',
  export: 'plus',
  schedule: 'standard',
  wishlist: 'standard',
  communication: 'standard',
  helpers: 'plus',
};

/**
 * Funksjonene med navn et menneske kan lese.
 *
 * Brukes i sammenligningstabellen på prislista. Rekkefølgen i `FUNKSJONER`
 * under er den tabellen vises i, og den følger planleggingen slik den faktisk
 * går for seg – ikke rekkefølgen funksjonene tilfeldigvis ble bygget i.
 */
export const NAVN_PAA_FUNKSJON: Record<Feature, string> = {
  guests: 'Gjesteliste',
  rsvp: 'Digital RSVP med egen svarlenke',
  communication: 'Egne e-poster til gjestene, og egen tekst i invitasjon og påminnelse',
  budget: 'Budsjett',
  tasks: 'Oppgaver og planlegging',
  schedule: 'Dagsprogram gjestene ser',
  wishlist: 'Ønskeliste',
  seating: 'Interaktiv bordplassering',
  export: 'Bordkart og gjesteliste som PDF',
  helpers: 'Deling med toastmaster og forlover',
};

export const FUNKSJONER: readonly Feature[] = [
  'guests',
  'rsvp',
  'communication',
  'budget',
  'tasks',
  'schedule',
  'wishlist',
  'seating',
  'export',
  'helpers',
];

/**
 * Verktøyene som er med i alle pakkene, men som ikke står i matrisen.
 *
 * De har ingen `Feature`, fordi ingen pakke sperrer dem. Prislista må likevel
 * si at de er med: en tabell over hva som inngår, der leverandørregisteret
 * ikke står, leses som at det ikke finnes – eller at det koster noe.
 *
 * Invitasjonen og påminnelsen med ferdig tekst står her og ikke under
 * `communication`: de sendes fra gjestelista på alle pakkene. Det Standard
 * legger til, er å skrive teksten selv.
 */
export const I_ALLE_PAKKENE: readonly string[] = [
  'Invitasjon og påminnelse på e-post, med ferdig tekst',
  'Leverandørregister',
];

export const PLAN_NAME: Record<Plan, string> = {
  free: 'Gratis',
  standard: 'Standard',
  plus: 'Plus',
};

export function planIncludes(plan: Plan, feature: Feature): boolean {
  return PLAN_RANK[plan] >= PLAN_RANK[FEATURE_MIN_PLAN[feature]];
}

export function requiredPlanFor(feature: Feature): Plan {
  return FEATURE_MIN_PLAN[feature];
}
